import * as React from "react";
import "../filters.scss";
import * as moment from "moment";
import RadioButton, {IRadioButton} from "../../../../hiberUiKit/src/atoms/Buttons/RadioButton";
import {none, option, Option} from "ts-option";
import {DateFilterType, DateFilterTypeName} from "./DateFilter";
import DateFilterModel from "../../../../model/workspace/DateFilterModel";
import DateUtils from "../../../../utils/DateUtils";
import {DateFilterValueItemApiDto} from "../../../../api/model/rest";

interface IProps {
	/**
	 * Input value
	 */
	value: Option<InRangeFilterModel>
	/**
	 * On change callback
	 * @param {Option<InRangeFilterModel>} value
	 */
	onChange: (value: Option<InRangeFilterModel>) => void
	/**
	 * Selected radio button Id
	 */
	selectedRadioButtonId: string
	/**
	 * On select button
	 * @param {string} id
	 */
	onSelectButton: (id: string) => void
	/**
	 * On error
	 * @param {string} error
	 */
	onError: (error: string) => void
}

interface IState {
	/**
	 * Start input value for "In Range"
	 */
	inputStartValue: string;
	/**
	 * End input value for "In Range"
	 */
	inputEndValue: string;
}


export default class InRangeFilter extends React.Component<IProps, IState> {

	constructor(props: IProps) {
		super(props);
		this.state = {
			inputStartValue: props.value.map(v => v.start).getOrElse(""),
			inputEndValue: props.value.map(v => v.end).getOrElse("")
		};
		this.onChangeInRangeStartInput = this.onChangeInRangeStartInput.bind(this);
		this.onChangeInRangeEndInput = this.onChangeInRangeEndInput.bind(this);
		this.parseInRangeInput = this.parseInRangeInput.bind(this);
		this.onBothInputsGiven = this.onBothInputsGiven.bind(this);
		this.resetFilter = this.resetFilter.bind(this);
	}

	componentWillReceiveProps(nextProps: IProps) {
		if (this.props.selectedRadioButtonId === this.radioButton.value &&
			nextProps.selectedRadioButtonId !== this.radioButton.value) {
			this.resetFilter();
		}
	}

	radioButton: IRadioButton = {
		label: DateFilterTypeName[DateFilterType.InRange],
		disabled: false,
		value: DateFilterType.InRange
	};

	render() {
		const radioButtonChecked = (this.props.selectedRadioButtonId == this.radioButton.value);
		const placeholder = radioButtonChecked ? "1y 2Q 3M 4d..." : null;
		return (
			<div className={"c-filter-dates_inputs_row"}>
				<RadioButton radioButton={this.radioButton}
							 onChange={() => {this.props.onSelectButton(this.radioButton.value)}}
							 checked={radioButtonChecked}/>
				<input className={"c-filter-dates_input_in-range_first-input"}
					   value={this.state.inputStartValue}
					   onChange={this.onChangeInRangeStartInput}
					   placeholder={placeholder}
					   disabled={!radioButtonChecked}/>
				<label className={"t-body c-filter-dates_inputs_row_last-label"}>to</label>
				<input className={"c-filter-dates_input_in-range"}
					   value={this.state.inputEndValue}
					   onChange={this.onChangeInRangeEndInput}
					   placeholder={placeholder}
					   disabled={!radioButtonChecked}/>
			</div>
		);
	}

	/**
	 * On change in "In range" start input
	 * @param {React.FormEvent<HTMLInputElement>} e
	 */
	onChangeInRangeStartInput(e: React.FormEvent<HTMLInputElement>) {
		this.setState({inputStartValue: e.currentTarget.value},
			() => {this.onBothInputsGiven()});
	}

	/**
	 * On change in "In range" end input
	 * @param {React.FormEvent<HTMLInputElement>} e
	 */
	onChangeInRangeEndInput(e: React.FormEvent<HTMLInputElement>) {
		this.setState({inputEndValue: e.currentTarget.value},
			() => {this.onBothInputsGiven()});
	}

	/**
	 * On both inputs given generate dates
	 */
	onBothInputsGiven() {
		let outputStartDate = this.parseInRangeInput(this.state.inputStartValue);
		let outputEndDate = this.parseInRangeInput(this.state.inputEndValue);

		if (outputStartDate.isDefined && outputEndDate.isDefined) {
			if (outputStartDate.get.isAfter(outputEndDate.get)) {
				this.props.onError("Start date should precede end date");
				this.props.onChange(none);
			}
			else {
				this.props.onError("");
				this.props.onChange(option(new InRangeFilterModel(this.state.inputStartValue, this.state.inputEndValue)));
			}
		}
		else {
			this.props.onChange(none);
		}
	}

	/**
	 * Parse input from "In range" section
	 * @param {string} i
	 */
	parseInRangeInput(i: string): Option<moment.Moment> {
		const parsed = DateUtils.parseRangeString(i);
		if (parsed instanceof Array)
			return none;
		return parsed;
	}

	/**
	 * Reset filter
	 */
	resetFilter() {
		this.setState({
			inputStartValue: "",
			inputEndValue: "",
		}, () => {this.props.onError("")});
	}
}

export class InRangeFilterModel extends DateFilterModel {
	constructor(public readonly start: string,
		public readonly end: string) {
		super(DateFilterType.InRange);
	}

	getDisplayValue(): string {
		return `${super.getDisplayValue()} ${this.start} - ${this.end}`;
	}

	toQueryJson(): (DateFilterValueItemApiDto | string)[] {
		const currentDate = moment().toISOString();
		return [DateUtils.parseToFilterValue(this.start), DateUtils.parseToFilterValue(this.end), currentDate]
	}

	static fromJson(json: DateFilterValueItemApiDto[]) {
		return new InRangeFilterModel(
			`${json[0].value}${DateUtils.parseFromFilterValue(json[0].rangeUnit)}`,
			`${json[1].value}${DateUtils.parseFromFilterValue(json[1].rangeUnit)}`);
	}
}